import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Tooltip,
  IconButton,
  styled,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import api from '../../services/api';
import useDebounce from '../../hooks/debounceHook';
import { useWorkspace } from '../../utils/WorkspaceContext';
import {
  formatFloatToTimeHM,
  DEFAULT_TIME_LOG_MINUTES,
} from '../../utils/timeFormatter';
import NoteLinkDialog from './NoteLinkDialog';
import CompactDateTimePicker from './CompactDateTimePicker';

/**
 * @typedef {Object} TimeLogNote
 * @property {number|null} id - Note ID (null until created)
 * @property {string} [_tempId] - Client key for unsaved rows
 * @property {string} notes - What the user worked on
 * @property {string} start_time
 * @property {string|null} end_time
 * @property {string} duration - Decimal hours, derived from the range
 * @property {'pending'} status - Only pending notes are listed; linked ones become time logs
 */

// Fixed layout so the time columns stay compact and Notes takes the rest.
const StyledTable = styled(Table)({
  tableLayout: 'fixed',
  '& .MuiTableCell-head': { fontWeight: 'bold' },
  '& .MuiTableCell-root': { padding: '5px 10px', borderBottom: 'none', verticalAlign: 'top' },
});

/**
 * Maps a date preset to the start_date/end_date query params.
 * @param {string} preset
 * @param {string} rangeStart - YYYY-MM-DD (custom only)
 * @param {string} rangeEnd - YYYY-MM-DD (custom only)
 * @returns {Object}
 */
export const dateParams = (preset, rangeStart, rangeEnd) => {
  const today = dayjs();
  const fmt = (d) => d.format('YYYY-MM-DD');
  switch (preset) {
    case 'today':
      return { start_date: fmt(today), end_date: fmt(today) };
    case 'last_3':
      return { start_date: fmt(today.subtract(2, 'day')), end_date: fmt(today) };
    case 'last_7':
      return { start_date: fmt(today.subtract(6, 'day')), end_date: fmt(today) };
    case 'last_month':
      return { start_date: fmt(today.subtract(1, 'month')), end_date: fmt(today) };
    case 'custom': {
      const params = {};
      if (rangeStart) params.start_date = rangeStart;
      if (rangeEnd) params.end_date = rangeEnd;
      return params;
    }
    default:
      return {};
  }
};

/**
 * Note Based time logging: users jot down what they did with a time range,
 * then AI links each note to an action item (reviewed in NoteLinkDialog).
 * @component
 */
const NoteBasedTimeLog = () => {
  const { currentWorkspace } = useWorkspace();
  const [notes, setNotes] = useState([]);
  const [datePreset, setDatePreset] = useState('today');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [error, setError] = useState('');

  // Unsaved rows are keyed by a temp id; edits queue here and flush debounced.
  // Refs (not state) so a flush always sees the latest queue and rows.
  const nextTempId = useRef(1);
  const pendingUpdates = useRef({});
  const inFlightCreates = useRef(new Set());
  const notesRef = useRef(notes);
  notesRef.current = notes;

  const fetchNotes = async () => {
    if (!currentWorkspace?.id) return;
    try {
      const response = await api.get('/time-log-notes/', {
        params: {
          workspace_id: currentWorkspace.id,
          status: 'pending',
          ...dateParams(datePreset, rangeStart, rangeEnd),
        },
      });
      const fetched = response.data.payload || [];
      // Keep rows still waiting for their first save.
      setNotes((prev) => [...prev.filter((n) => !n.id), ...fetched]);
    } catch (err) {
      console.error('Error fetching time log notes:', err);
      setNotes((prev) => prev.filter((n) => !n.id));
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, datePreset, rangeStart, rangeEnd]);

  const flushUpdates = useDebounce(async () => {
    const batch = pendingUpdates.current;
    if (Object.keys(batch).length === 0) return;
    pendingUpdates.current = {};

    const requeue = (key, updates) => {
      pendingUpdates.current[key] = { ...updates, ...(pendingUpdates.current[key] || {}) };
    };
    const mergeServerRow = (matches, data) =>
      setNotes((prev) =>
        prev.map((n) => (matches(n) ? { ...n, id: data.id, duration: data.duration, status: data.status } : n))
      );

    await Promise.all(
      Object.entries(batch).map(async ([key, updates]) => {
        const row = notesRef.current.find((n) => String(n.id) === key || n._tempId === key);
        const isNew = key.startsWith('new-');
        try {
          if (isNew && row?.id) {
            // Created since these edits were queued.
            const response = await api.patch(`/time-log-notes/${row.id}/`, updates);
            mergeServerRow((n) => n.id === row.id, response.data);
          } else if (isNew) {
            if (inFlightCreates.current.has(key)) {
              requeue(key, updates);
              return;
            }
            inFlightCreates.current.add(key);
            try {
              const response = await api.post('/time-log-notes/', {
                workspace_id: currentWorkspace.id,
                notes: row?.notes || '',
                start_time: row?.start_time,
                end_time: row?.end_time,
                ...updates,
              });
              mergeServerRow((n) => n._tempId === key, response.data);
            } finally {
              inFlightCreates.current.delete(key);
              if (Object.keys(pendingUpdates.current).length) flushUpdates();
            }
          } else {
            const response = await api.patch(`/time-log-notes/${key}/`, updates);
            mergeServerRow((n) => String(n.id) === key, response.data);
          }
          setError('');
        } catch (err) {
          console.error('Error saving time log note:', err);
          setError('Some notes could not be saved. Check the times and try again.');
        }
      })
    );
  });

  /**
   * Updates a row locally and queues the change for the server.
   * @param {TimeLogNote} note
   * @param {Object} fields
   */
  const updateNote = (note, fields) => {
    const key = String(note.id ?? note._tempId);
    const updated = { ...note, ...fields };
    // Duration follows the range (mirrors the server-side rule).
    if (updated.start_time && updated.end_time) {
      updated.duration = Math.max(dayjs(updated.end_time).diff(dayjs(updated.start_time), 'minute') / 60, 0).toFixed(2);
    }
    setNotes((prev) => prev.map((n) => ((n.id ?? n._tempId) === (note.id ?? note._tempId) ? updated : n)));
    pendingUpdates.current[key] = { ...(pendingUpdates.current[key] || {}), ...fields };
    flushUpdates();
  };

  const handleAddNote = () => {
    const start = dayjs().second(0).millisecond(0);
    const tempId = `new-${nextTempId.current++}`;
    const row = {
      id: null,
      _tempId: tempId,
      notes: '',
      start_time: start.format(),
      end_time: start.add(DEFAULT_TIME_LOG_MINUTES, 'minute').format(),
      duration: (DEFAULT_TIME_LOG_MINUTES / 60).toFixed(2),
      status: 'pending',
    };
    setNotes((prev) => [row, ...prev]);
    // Save right away so the draft survives a reload even if left blank.
    pendingUpdates.current[tempId] = {};
    flushUpdates();
  };

  const handleDelete = async (note) => {
    const key = String(note.id ?? note._tempId);
    delete pendingUpdates.current[key];
    setNotes((prev) => prev.filter((n) => (n.id ?? n._tempId) !== (note.id ?? note._tempId)));
    if (!note.id) return;
    try {
      await api.delete(`/time-log-notes/${note.id}/`);
    } catch (err) {
      console.error('Error deleting time log note:', err);
      fetchNotes();
    }
  };

  const pendingNotes = useMemo(() => notes.filter((n) => n.id && (n.notes || '').trim()), [notes]);

  const totalDuration = useMemo(
    () => notes.reduce((sum, n) => sum + (parseFloat(n.duration) || 0), 0),
    [notes]
  );

  // Push any debounced edits first so the AI reads what is on screen.
  const handleOpenLinkDialog = async () => {
    await flushUpdates.flush?.();
    setLinkDialogOpen(true);
  };

  // Linked notes are now time logs on the other tabs, so drop them here.
  const handleLinked = (linkedIds) => {
    setLinkDialogOpen(false);
    const linked = new Set(linkedIds);
    setNotes((prev) => prev.filter((n) => !linked.has(n.id)));
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNote}>
          Add Note
        </Button>
        <Tooltip title={pendingNotes.length ? '' : 'Add notes with some text to link them'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              disabled={!pendingNotes.length}
              onClick={handleOpenLinkDialog}
            >
              Auto-link Notes{pendingNotes.length ? ` (${pendingNotes.length})` : ''}
            </Button>
          </span>
        </Tooltip>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Date</InputLabel>
          <Select value={datePreset} label="Date" onChange={(e) => setDatePreset(e.target.value)}>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="last_3">Last 3 days</MenuItem>
            <MenuItem value="last_7">Last 7 days</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        {datePreset === 'custom' && (
          <>
            <TextField
              type="date"
              label="From"
              size="small"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>

      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell>Notes</TableCell>
            {/* Percent of the table width so columns scale with the screen. */}
            <TableCell sx={{ width: '16%' }}>Start Time</TableCell>
            <TableCell sx={{ width: '16%' }}>End Time</TableCell>
            <TableCell sx={{ width: '8%' }}>Duration</TableCell>
            <TableCell sx={{ width: '6%' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {notes.map((note) => (
            <TableRow key={note.id ?? note._tempId}>
              <TableCell>
                <TextField
                  value={note.notes || ''}
                  onChange={(e) => updateNote(note, { notes: e.target.value })}
                  placeholder="What did you work on?"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={1}
                  fullWidth
                />
              </TableCell>
              {['start_time', 'end_time'].map((field) => (
                <TableCell key={field}>
                  <CompactDateTimePicker
                    value={note[field]}
                    onChange={(value) => updateNote(note, { [field]: value })}
                  />
                </TableCell>
              ))}
              <TableCell>
                <TextField value={formatFloatToTimeHM(note.duration)} disabled variant="outlined" size="small" fullWidth />
              </TableCell>
              <TableCell>
                <IconButton size="small" aria-label="Delete note" onClick={() => handleDelete(note)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {notes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No notes for this period. Click "Add Note" to write what you worked on.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                  Total:
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {formatFloatToTimeHM(totalDuration)}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>

      {linkDialogOpen && (
        <NoteLinkDialog
          open={linkDialogOpen}
          notes={pendingNotes}
          workspaceId={currentWorkspace?.id}
          onClose={() => setLinkDialogOpen(false)}
          onSaved={handleLinked}
        />
      )}
    </Box>
  );
};

export default NoteBasedTimeLog;
